// ---------------------------------------------------------------------------
// Firestore — Serviço da coleção `users`
// Schema conforme lura-context.md §4
// ---------------------------------------------------------------------------

import {
  doc,
  getDoc,
  getDocFromCache,
  getDocs,
  onSnapshot,
  query,
  collection,
  where,
  limit,
  serverTimestamp,
  setDoc,
  updateDoc,
  type FieldValue,
  type Timestamp,
  type DocumentSnapshot,
} from "firebase/firestore";
import { resilientOnSnapshot } from "../../lib/firebase/resilientSnapshot";
import { withReadTimeout } from "../../lib/firebase/readTimeout";
import { db } from "../../lib/firebase/config";
import { isThemeAcceptedAtOnboarding } from "../../config/themes";
import { MAX_HIGHLIGHTED_BADGES } from "../../features/gamification/badgeHighlightConstants";
import { createUserStatsIfMissing } from "./statsService";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

/**
 * Recorte gravado de uma imagem de perfil. Espelha `ProfileImageSource` de
 * `features/profile/lib/imageCrop` — declarado aqui, e não importado de
 * `features/`, para o serviço de dados não depender de uma feature.
 */
export interface ProfileImageSourceData {
  publicId: string;
  version: number | null;
  /** Dimensões do master no Cloudinary. */
  width: number;
  height: number;
  crop: { x: number; y: number; width: number; height: number };
}

/**
 * Estado de moderação. `pending` e `rejected` NUNCA acompanham uma URL
 * pública — ver `setProfileImage`.
 */
export type ProfileImageModeration = "none" | "approved" | "pending" | "rejected";

export interface UserProfile {
  uid: string;
  email: string;
  /** Username único — compõe a URL pública /profile/:username */
  username: string;
  /** Nome de exibição (sincronizado do Firebase Auth ou editado pelo usuário) */
  displayName: string;
  /** URL do avatar (Google Auth ou string vazia) */
  photoURL: string;
  /** Bio pública exibida no perfil */
  bio: string;
  themePreference: string;
  /** Alias estável do tema (espelha themePreference; usado em perfil público). */
  themeId?: string;
  /**
   * O tema que a pessoa usava antes de equipar um tema de EVENTO (Halloween,
   * `config/themeEvents.ts`). Gravado junto do `themePreference` só ao equipar
   * o tema de evento (`themeEquipUpdate`); é para onde ela volta quando o
   * evento fecha. Preferência de exibição, client-writable (string ≤ 40 nas
   * Rules), lido pelo resolvedor nas três superfícies e no servidor.
   */
  themeBeforeEvent?: string;
  readingGoal: number;
  /** Total de livros lidos ao longo da vida (auto-declarado no onboarding) */
  lifetimeBooksRead: number;
  /** Quantidade de livros físicos na estante (auto-declarado no onboarding) */
  ownedBooksCount: number;
  favoriteGenres: string[];
  onboardingCompleted: boolean;
  /** @deprecated legado; estatísticas oficiais vivem em `user_stats/{uid}`. */
  stats?: {
    totalBooksRead: number;
    totalPagesRead: number;
    currentStreak: number;
    /** Páginas lidas por dia (chave ISO `yyyy-MM-dd`). */
    dailyPages?: Record<string, number>;
  };
  badges: string[];
  /** Última sequência antes de reset (usada em recuperação de ofensiva). */
  lastStreak?: number;
  /** Recuperação gratuita única já consumida. */
  freeStreakRecoveryUsed?: boolean;
  /** Tickets restantes para recuperar ofensiva manualmente. */
  streakTickets?: number;
  /** Série diária legada para gráfico semanal. */
  dailyPages?: Record<string, number>;
  /**
   * Faucets de ativação já concedidos (client-locked — só Cloud Functions
   * escrevem, via triggers). Serve só pra UI saber o que já foi ganho; a
   * autoridade da concessão é server-side. Ver `docs/systems/faucets-home.md`.
   */
  growthFaucets?: {
    setup?: Partial<Record<"first_book" | "first_pages" | "first_focus" | "profile_custom", boolean>>;
    firstFollow?: boolean;
  };
  /** Contador desnormalizado de seguidores. */
  followersCount: number;
  /** Contador desnormalizado de perfis seguidos. */
  followingCount: number;
  /** Data da última atividade de leitura registrada (formato "YYYY-MM-DD"). */
  lastReadDate?: string;
  /**
   * 1º dia lido da vida da conta ("YYYY-MM-DD", BR) — marco gravado só pelo
   * servidor (ativação 07/10; backfill para contas antigas). Dirige o cartão
   * "Dia 1", a linha do dia 2 e a oferta do PRO no 2º dia lido
   * (`features/home/firstWeek.ts`).
   */
  firstReadDate?: string;
  /** Oculta capas e conteúdos adultos nas sugestões. */
  safeMode?: boolean;
  /**
   * Credencial de afiliado do creator — a conta DELE no programa, que é quem
   * paga (o Livra ficou fora do cash flow em 19/05). Nula = sem conta; o link
   * cai na tag da casa (regra ③).
   *
   * Client-locked desde 20/07: só a callable `setCreatorTags` escreve, atrás
   * do guard de creator ativo.
   *
   * ⚠️ ENCOLHEU DE 4 REDES PARA 1 EM 05/08/2026 (decisão do fundador): Amazon,
   * Shopee e Magalu saíram do canal de tag própria e usam a tag da casa em
   * qualquer contexto. As chaves antigas podem existir em docs gravados antes
   * disso e são IGNORADAS na leitura — o gate é `CREATOR_TAG_NETWORKS` em
   * `features/affiliates/lib/referralPrograms.ts`.
   *
   *  - awin: **affiliate ID numérico da conta Awin do creator** — vai no
   *    `awinaffid` do link. ⚠️ Mudou em 20/07: era um `clickref` de texto
   *    livre enquanto a comissão caía na conta do Livra (modelo middleman,
   *    descartado). Hoje o `clickref` carrega o uid do creator só como
   *    rastreio; quem recebe é o dono do `awinaffid`.
   */
  creatorTags?: {
    awin?: string | null;
  };
  /** Assinante Livra PRO (selo e temas exclusivos). */
  isPro?: boolean;
  /**
   * Fim do último período de PRO, em ms. Escrito pelo resolver
   * (`syncEntitlementForUser`) INCONDICIONALMENTE — sobrevive à revogação, e é
   * por isso que serve para dizer "seu PRO valeu até X" depois que `isPro`
   * caiu (é o que abre o `ProLapseWatcher`). `null` para quem nunca teve PRO
   * e para o vitalício. Já chegava no doc; só faltava o tipo.
   */
  proExpiresAt?: number | null;
  /**
   * Quando esta conta entrou em trial pela PRIMEIRA vez (ms), em qualquer
   * canal. Carimbo monotônico do resolver — nunca some depois de setado. É o
   * que o checkout do Stripe lê para não conceder um segundo trial (16/09,
   * quando o anual também ganhou os 7 dias), e o que a vitrine web lê para
   * não PROMETER o que o servidor vai negar. `null`/ausente = nunca testou.
   */
  proTrialConsumedAt?: number | null;
  /** Canal que banca o PRO AGORA (`null` sempre que `isPro` é false). */
  proSource?: string | null;
  /** Acesso ao painel de administração. Definido apenas via Admin SDK. */
  isAdmin?: boolean;
  /** IDs de temas desbloqueados com moedas (ex.: "cyberpunk", "paper"). */
  unlockedThemes?: string[];
  /** Tags de legado (ex.: pioneer). "founder" é DERIVADA de
   *  `subscriptions/{uid}.isLifetime` pelo trigger `onSubscriptionWritten` —
   *  não escreva à mão (BUG-72). */
  legacyTags?: string[];
  /** Número do Founder (1…5000), denormalizado de `subscriptions/{uid}` pelo
   *  mesmo trigger. Existe aqui porque `subscriptions` é isOwner-only e o
   *  perfil público precisa exibir o serial. Client-locked. */
  founderSerial?: number | null;
  /**
   * A pessoa é Founder E segue com uma assinatura ativa NESTA loja
   * (`"app_store" | "play_store"`), que só ela pode cancelar.
   *
   * Escrito pelo mint de Founder da loja (`founderFromRevenueCat`) e apagado
   * quando a loja informa que a assinatura acabou. Existe porque o client **não
   * consegue ler `subscriptions/{uid}/sources/*`** — regra de Firestore não é
   * recursiva, e a subcoleção é deny-by-default.
   *
   * Guarda a loja, não um boolean, porque o aviso precisa nomeá-la. Ausente =
   * nada a avisar. Client-locked. Ver decisão de 20/08, cenário 2.
   */
  founderStoreSubscriptionPending?: "app_store" | "play_store" | null;
  /** IDs das badges fixadas na vitrine pública do perfil (máx. 3). */
  highlightedBadges?: string[];
  /** URL da imagem de banner do perfil (PRO). Client-locked desde 28/07 —
   *  só `setProfileImage`/`clearProfileImage` escrevem. */
  bannerUrl?: string;
  /**
   * Cor sólida do banner (GRÁTIS, id de `constants/bannerColors`). É
   * client-writable — ao contrário de `bannerUrl` — porque não há UGC a
   * moderar nem privilégio a conceder: o valor é um id de uma lista fechada,
   * e as Rules recusam qualquer outro. Perde para a imagem na exibição, e por
   * isso as duas coisas convivem no doc sem uma apagar a outra.
   */
  bannerColor?: string;
  /**
   * Origem do avatar no Cloudinary + recorte escolhido pelo usuário
   * (onda de imagens de perfil, parte 2 — BUG-56). Client-locked.
   *
   * Guardar `publicId` + `crop` em vez de só a URL é o que permite
   * reenquadrar depois SEM novo upload: o editor recarrega o master e o
   * recorte vira uma transformação `c_crop` na entrega.
   */
  avatarSource?: ProfileImageSourceData;
  bannerSource?: ProfileImageSourceData;
  /** Veredito de moderação da imagem. Client-locked (parte 3, requisito de
   *  beta). Só `approved` é exibido — ver `setProfileImage`. */
  avatarModeration?: ProfileImageModeration;
  bannerModeration?: ProfileImageModeration;
  /**
   * Música do perfil (PRO) — Fase 1 da frente de 03/08.
   * Client-locked: só `setProfileMusic`/`clearProfileMusic` escrevem, e o
   * campo é bloqueado nas duas whitelists de Rules (update) e na blocklist do
   * create. Ver `docs/systems/spotify-profile-music.md`.
   *
   * ⚠️ O campo PERSISTE quando o PRO expira — a exibição é que desliga (o
   * gate lê o `isPro` CORRENTE, nunca um snapshot). Reassinar religa sem o
   * usuário precisar colar o link de novo.
   *
   * Guarda o `trackId`, não uma URL: o iframe do player é montado a partir do
   * id revalidado, então não existe string de URL no doc pra alguém renderizar
   * por engano.
   */
  profileMusic?: {
    /** base62, 22 chars. Revalidar antes de montar qualquer URL. */
    trackId: string;
    title?: string | null;
    /** `null` só quando a metadata veio do oEmbed — ele não devolve artista. */
    artist?: string | null;
    thumbnailUrl?: string | null;
    /** `api` = Web API autenticada (tem artista); `oembed` = fallback sem ele. */
    metadataSource?: "cache" | "api" | "oembed" | null;
    /** `null` = nenhuma metadata foi obtida (estado OEMBED_FALHOU). */
    metadataFetchedAt?: Timestamp | null;
    setAt?: Timestamp;
  };
  /**
   * Tema gerado da capa da leitura atual (PRO) — F2 do Roadmap PRO, 13/09.
   * Client-locked: só `generateReadingTheme`/`clearReadingTheme` escrevem, e
   * o campo é bloqueado nas duas travas de Rules (fora da whitelist de
   * update, dentro da blocklist de create) — mesma classe de `profileMusic`
   * acima. Ver `docs/systems/reading-theme.md`.
   *
   * O PONTEIRO pro tema é `themePreference: "current_reading"` (client-safe,
   * igual a qualquer outro tema — o PRO-gate mora no resolvedor, não aqui).
   * Este campo guarda só o CONTEÚDO que o ponteiro aponta: os 46 tokens já
   * aprovados no gate de contraste do servidor.
   */
  currentReadingTheme?: {
    tokens: Record<string, string>;
    /** `userBookId` que gerou — desde 27/09 o servidor confere que é da pessoa e está "lendo"/"lido". */
    userBookId: string;
    generatedAt: Timestamp;
    /** 27/09 (v3): a capa RESOLVIDA que gerou o tema — parte da chave de "já gerado" (BUG-297). */
    coverUrl?: string | null;
    /** 27/09 (v3): papel claro ou escuro. Ausente = tema v2. */
    mode?: "light" | "dark";
    /** 27/09: motor que gerou (2 = ausente/legado, 3 = cor da capa no papel). */
    engineVersion?: number;
  } | null;
  /**
   * Tema da capa v3 (27/09): o seletor Claro · Escuro · Automático, lembrado
   * por pessoa. Client-safe (whitelist + enum nas Rules). Ausente = `auto`.
   */
  readingThemeModePref?: "auto" | "light" | "dark";
  /**
   * Redes sociais do perfil (01/09) — mapa `rede → handle` em charset fechado,
   * validado nas Rules (enum de chaves + `[a-z0-9._-]{1,30}` por valor).
   * Guarda o HANDLE, nunca URL: a URL é derivada de uma base fixa por rede em
   * `features/profile/constants/socialLinks.ts`, que é também quem decide
   * quais chaves existem. O tipo fica largo (`Record<string, string>`) de
   * propósito — chave desconhecida no doc é descartada na leitura
   * (`resolveSocialLinks`), não erro de tipo.
   */
  socialLinks?: Record<string, string>;
  /** ID da cor/gradiente do nome de exibição (PRO). */
  usernameColor?: string;
  /** IDs de flairs exibidos no perfil (PRO, máx. 5). */
  flairs?: string[];
  /**
   * Selos de gamificação na fileira do hero (05/08). A fileira tem teto de 3
   * (`MAX_PROFILE_CHIPS`) e estes dois dizem se liga e TOP N gastam vaga.
   *
   * ⚠️ Ausente = LIGADO nas duas superfícies. O campo nasceu depois da base, e
   * ler ausência como `false` apagaria o chip de liga de todo mundo. Sempre
   * `!== false`, nunca `=== true`.
   */
  showLeagueChip?: boolean;
  showEliteChip?: boolean;
  /**
   * Liga em modo silencioso (13/09): esconde o card da Home e faz o servidor
   * recusar todo push `league_*` (`sendPushToUser`). A pessoa continua na sala
   * e continua pontuando. ⚠️ Ausente = DESLIGADO (`=== true`), o inverso dos
   * dois acima — este é um silêncio novo, e silêncio precisa significar "não
   * pedi", não "quero".
   */
  leagueQuietMode?: boolean;
  /**
   * Hora (BR, 8–22) em que a pessoa quer o lembrete de leitura (ativação,
   * 07/10, `redesign-livra/ativacao/` D5). Escolhida no convite de push ou em
   * Ajustes. Vence o horário MEDIDO (`readingHourBR`, escrito pelo digest) nos
   * dois avisos que lembram de ler: a ofensiva em risco e o convite a quem
   * parou. Ausente/`null` = automático (20h para a ofensiva; medido ou 19h para
   * o convite). Validado nas Rules (inteiro 8–22).
   */
  readingReminderHourBR?: number | null;
  /**
   * Saiu de "Leitores parecidos" (01/10, `docs/systems/people-suggestions.md`
   * §5): não vê a lista e não é sugerido a ninguém. ⚠️ Campo NEGATIVO de
   * propósito — ausente = participa, `=== true` = saiu (mesma leitura natural
   * do `leagueQuietMode`). Ler sempre por `isPeopleSuggestionsEnabled`.
   */
  peopleSuggestionsOptOut?: boolean;
  /**
   * Mostra as notas públicas do dono na vitrine `/lendo/:username` (F4,
   * 12/09). Não é `isPublic` da nota — a nota já é pública no mural do
   * livro de qualquer forma; isto só decide se ela também aparece na
   * página de maior alcance (link-na-bio, compartilhável).
   *
   * ⚠️ Ausente = DESLIGADO — o INVERSO de `showLeagueChip`/`showEliteChip`.
   * Lá o campo nasceu depois de uma UI que já existia pra todo mundo
   * (ausente não pode apagar o que já estava ligado). Aqui é uma vitrine
   * NOVA: silêncio precisa significar "não pedi para expor", não "não pedi
   * para tirar". Ver docs/systems/lendo-page.md §4. Sempre `=== true`,
   * nunca `!== false`.
   */
  showLendoNotes?: boolean;
  /** Flairs ATIVOS do decoupling F-Econ (cap 2) — client-locked, só a
   * callable setActiveCosmetic escreve (não entra em UpdatableProfile). */
  activeFlairs?: string[];
  /** Frente G — moldura de avatar equipada. Client-locked (setActiveCosmetic). */
  activeAvatarBorder?: string | null;
  /** A3 (31/08) — banner cosmético equipado. Client-locked (setActiveCosmetic).
   *  Ganha da `bannerColor` e perde para o `bannerUrl`; ver
   *  `resolveBannerSurface`. */
  activeBanner?: string | null;
  /** Mascote equipado (13/10, `docs/systems/pets.md`). Client-locked: só
   *  `setActiveCosmetic` kind "pet" escreve; nunca entra em UpdatableProfile.
   *  O perfil público lê o MESMO campo do MESMO doc (BUG-80). */
  activePet?: string | null;
  /** Frente G (ELITE) — posição no ranking global da última semana fechada.
   *  Client-locked (só closeWeeklyLeagues escreve). Exibido como selo "TOP N"
   *  enquanto recente (ver isEliteRankActive em leagueService). */
  eliteRank?: number | null;
  eliteRankedAt?: Timestamp | null;
  /**
   * Tokens FCM ativos do usuário (multi-device). Sprint 4 (Push Web). Cap
   * por `FCM_TOKENS_MAX` (5). Itens são strings opacas devolvidas por
   * `getToken()` do `firebase/messaging`.
   */
  fcmTokens?: string[];
  /** Opt-in explícito para receber push. Default não-existe (silent). */
  notificationsOptIn?: boolean;
  /**
   * IDs de milestones de share já exibidos a este usuário (B2 share moments).
   * Dedup: cliente só mostra o modal se o id ainda não está aqui.
   * Schema: array de strings tipo `pages_1000`, `books_10`, `goal_2026`.
   */
  shownShareMilestones?: string[];
  /**
   * Ids de conquista cujo CARTÃO este usuário já viu (BUG-281). Baseline do
   * `useBadgeAnnounceWatcher`: `badges − badgesAnnounced` = o que celebrar.
   * Client-safe (rules: lista ≤ 100, `arrayUnion`). Não libera nada —
   * `badges` (client-locked) segue a única prova de conquista. Ausente =
   * conta anterior ao campo: na primeira carga celebra tudo que está em
   * `badges` (decisão de 21/09 — o backfill de 425 badges nunca foi anunciado).
   */
  badgesAnnounced?: string[];
  /**
   * Último mês ("YYYY-MM") cuja retrospectiva a pessoa VIU (Estatísticas v2,
   * 26/09). Some com o card da Home. Client-safe: forjar só esconde ou reexibe
   * o próprio card — rules validam o formato.
   */
  recapSeenMonth?: string;
  /** Último ano ("YYYY") cujo Wrap a pessoa VIU (fatia 5). Mesmo desenho do `recapSeenMonth`. */
  wrapSeenYear?: string;
  /**
   * Reading Personality do mês corrente — escrita pelo cron mensal
   * `monthlyPersonalityCron`. Cliente lê `id` para abrir Studio em modo
   * personality. `month` é "YYYY-MM" para dedup do modal mensal.
   */
  monthlyPersonality?: {
    id: string;
    month: string;
    computedAt?: FieldValue;
  };
  /**
   * Programa Creator B2B (B3 Fase 4). Opt-in explícito — Free não tem
   * dashboard nem revshare. Quando true, callable `logAffiliateClick`
   * atribui clicks ao perfil deste user em `affiliate_clicks/{uid}_{month}`.
   */
  creatorOptIn?: boolean;
  /**
   * Chave PIX do modelo middleman (dormente — `requestCreatorPayout` foi
   * desativada em 20/07, o creator recebe direto do programa parceiro).
   */
  creatorPixKey?: string | null;
  /** Quando o código de ativação foi resgatado. Client-locked. */
  creatorActivatedAt?: Timestamp | null;
  /** Código de ativação usado, para auditoria. Client-locked. */
  creatorActivationCode?: string | null;
  /**
   * Código de indicação DESTE creator — espelho de `creator_codes/{CODE}`
   * pro client exibir sem precisar de mais uma leitura. Imutável: nasce do
   * @ na ativação e não acompanha troca de @. Client-locked.
   */
  referralCode?: string | null;
  /**
   * uid do creator que indicou este usuário. **Primeira atribuição vence** —
   * nunca é sobrescrito. Client-locked.
   */
  referredBy?: string | null;
  /** Fim da janela de atribuição de referral (90 dias). Client-locked. */
  referralExpiresAt?: Timestamp | null;
  /**
   * Metas avançadas opcionais (B6 — Reading insights).
   *  - `pagesPerYear`: total de páginas a ler no ano atual.
   *  - `genres`: meta de livros por gênero (chave = canonical key de
   *    `book_meta.categories`, valor = quantos livros).
   *  - `femaleAuthors` / `debutAuthors`: schema reservado; UI ainda não os
   *    rastreia (depende de enrichment em `book_meta` — Sprint 4).
   */
  goalsExtended?: {
    pagesPerYear?: number;
    genres?: Record<string, number>;
    femaleAuthors?: number;
    debutAuthors?: number;
  };
  createdAt?: FieldValue;
  updatedAt?: FieldValue;
  // ── Ligas ──────────────────────────────────────────────────────────────────
  /** Liga atual do utilizador (atribuída pelo cron semanal). */
  currentLeague?: "bronze" | "silver" | "gold" | "diamond";
  /** Tier antes do último closeWeeklyLeagues — usado para detectar promoção/rebaixamento no cliente. */
  previousLeague?: "bronze" | "silver" | "gold" | "diamond";
  /** ID da sala de liga global da semana corrente. */
  currentRoomId?: string | null;
  /** Identificador ISO da semana da sala global (ex: "2026_W16"). */
  currentRoomWeek?: string | null;
  /** IDs das salas de ligas privadas activas (máx. 5). */
  privateRoomIds?: string[];
  /** Dia UTC (YYYY-MM-DD) em que foi creditado o último bónus SOCIAL_SHARE_BONUS (servidor). */
  lastSocialShareRewardDay?: string;
  /** Pedido de exclusão de conta (LGPD) — escritos apenas via Cloud Functions. */
  accountDeletionRequestedAt?: Timestamp;
  /** Data/hora após a qual a conta pode ser removida (carência de 7 dias). */
  accountDeletionScheduledFor?: Timestamp;
}

// Campos que updateUserProfile pode receber do cliente.
// Campos sensíveis (isPro, currentLeague, unlockedThemes, legacyTags,
// streakTickets, freeStreakRecoveryUsed, badges, lastStreak, lastReadDate,
// lifetimeBooksRead, ownedBooksCount) são escritos exclusivamente via Cloud Functions.
export type UpdatableProfile = Partial<
  Pick<
    UserProfile,
    | "displayName"
    // `photoURL` e `bannerUrl` saíram daqui (28/07, onda de imagens de
    // perfil): viraram client-locked. Só `setProfileImage` e
    // `clearProfileImage` escrevem, e a primeira só publica o que a moderação
    // liberou. Enquanto eram client-writable, moderar o upload no Cloudinary
    // não impedia ninguém de apontar o avatar para uma URL arbitrária — o
    // gate tem que estar no write do campo, não no upload.
    | "bio"
    // `username` saiu daqui: virou client-locked nas Rules e só a callable
    // `claimUsername` escreve (reserva transacional em `usernames/`).
    | "themePreference"
    | "themeId"
    // Tema de antes do tema de evento (07/10) — mesma classe do
    // `themePreference`, string ≤ 40 validada nas Rules.
    | "themeBeforeEvent"
    | "readingGoal"
    | "favoriteGenres"
    | "safeMode"
    // `creatorTags`, `creatorOptIn` e `creatorPixKey` saíram daqui (20/07):
    // viraram client-locked nas Rules. `creatorOptIn` é a credencial de
    // creator e só `redeemCreatorCode` concede; `creatorTags` decide pra
    // onde vai comissão de afiliado e só `setCreatorTags` escreve, atrás do
    // guard de creator ativo.
    | "highlightedBadges"
    | "usernameColor"
    // A COR do banner é editável pelo client; a IMAGEM (`bannerUrl`) não. A
    // linha que separa as duas não é "banner", é moderação: cor é um id de
    // lista fechada validada nas Rules, imagem é conteúdo enviado.
    | "bannerColor"
    // Handles de rede social — client-writable porque o valor é handle em
    // charset fechado validado nas Rules, nunca URL (ver o docblock do campo).
    | "socialLinks"
    | "flairs"
    // Preferência de EXIBIÇÃO, não de posse: escondem o chip de liga/TOP N no
    // hero. O valor dos campos que os alimentam (`currentLeague`, `eliteRank`)
    // segue client-locked — o dono escolhe o que mostrar, não o que é.
    | "showLeagueChip"
    | "showEliteChip"
    // Modo silencioso da liga: preferência de COMUNICAÇÃO (card + push), não
    // de posse — `currentLeague` e a sala seguem client-locked.
    | "leagueQuietMode"
    // Opt-out de "Leitores parecidos": preferência, não privilégio (whitelist
    // `userOwnSafeFields` nas Rules, `is bool`).
    | "peopleSuggestionsOptOut"
    | "showLendoNotes"
    | "goalsExtended"
    // Claro · Escuro · Automático do tema da leitura (v3, 27/09): preferência
    // de EXIBIÇÃO — o tema em si (`currentReadingTheme`) segue client-locked.
    | "readingThemeModePref"
    // Hora do lembrete de leitura (ativação 07/10): preferência de
    // comunicação, inteiro 8–22 validado nas Rules.
    | "readingReminderHourBR"
  >
>;

/**
 * Garante tipos estáveis ao ler do Firestore (campos novos podem estar ausentes).
 */
export function normalizeUserProfile(raw: UserProfile): UserProfile {
  const tags = raw.legacyTags;
  const legacyTags = Array.isArray(tags)
    ? tags.filter((t): t is string => typeof t === "string")
    : [];
  const themePrefRaw = raw.themePreference ?? "default";
  const themePref = themePrefRaw === "custom" ? "noir" : themePrefRaw;
  const themeIdRaw = raw.themeId ?? themePref;
  const themeId = themeIdRaw === "custom" ? "noir" : themeIdRaw;
  return {
    ...raw,
    isPro: raw.isPro === true,
    legacyTags,
    themeId,
    themePreference: themePref,
    followersCount: typeof raw.followersCount === "number" ? raw.followersCount : 0,
    followingCount: typeof raw.followingCount === "number" ? raw.followingCount : 0,
  };
}

const buildUsername = (displayName: string | null, uid: string): string => {
  const base = (displayName ?? "leitor")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return `${base || "leitor"}_${uid.slice(0, 6)}`;
};

/** "joao.pedro_silva" \u2192 "Joao Pedro Silva" \u2014 base digna pro perfil quando o
 * provider n\u00e3o traz nome (cadastro por email+senha). */
const humanizeEmailPrefix = (prefix: string): string =>
  prefix
    .split(/[._\-+]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

// ---------------------------------------------------------------------------
// Operações públicas
// ---------------------------------------------------------------------------

/**
 * Garante que o documento do usuário existe no Firestore.
 * Chamado após cada login — cria o perfil na primeira vez, retorna o existente nas demais.
 *
 * Operações Firestore vs regras:
 * - `getDoc(users/{uid})` → read (qualquer autenticado pode ler perfis).
 * - `setDoc(users/{uid}, …)` sem merge quando o doc não existe → **create** (`request.auth.uid == userId`).
 * - `createUserStatsIfMissing` → **create** em `user_stats/{uid}` (mesmo uid).
 *
 * ── Cache primeiro (BUG-260, 16/09) ─────────────────────────────────────────
 * Esta função está no caminho crítico do boot, atrás de `BOOT_PROFILE_TIMEOUT`
 * (10 s). `getDoc` espera o SERVIDOR, e só cai para o cache quando o SDK
 * conclui que está offline — o que, com o navegador dizendo "conectado" e a
 * rede não passando pacote, leva ~10 s de tentativas. Empate com o teto do
 * boot, e o boot perdia: tela de falha com rede ruim, enquanto com a rede
 * DESLIGADA (o SDK sabe na hora) o app abria do cache. Era o relato de 16/09.
 *
 * Para quem já entrou neste aparelho o perfil está no cache, e é isso que
 * decide: `getDocFromCache` assenta em ms e o boot não espera servidor nenhum
 * — o `onSnapshot` do `AuthContext` traz a versão fresca quando a rede
 * responder. `createUserStatsIfMissing` corre em segundo plano nesse caminho:
 * a invariante (stats existe para todo perfil) já valeu na 1ª entrada, e um
 * `getDoc` de servidor aqui devolveria exatamente a espera que se quer tirar.
 * Cache vazio (1ª entrada neste aparelho, cache purgado, persistência
 * indisponível) segue o caminho de servidor, que é o único possível.
 */
export const ensureUserProfile = async (params: {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}): Promise<UserProfile> => {
  const { uid, email, displayName, photoURL } = params;
  const userRef = doc(db, "users", uid);

  const cached = await getDocFromCache(userRef).catch(() => null);
  if (cached?.exists()) {
    void createUserStatsIfMissing(uid).catch((err: unknown) => {
      console.warn("[Lura] createUserStatsIfMissing (segundo plano):", (err as Error).message);
    });
    return normalizeUserProfile(cached.data() as UserProfile);
  }

  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    // Cadastro por email+senha não traz displayName do provider — deriva do
    // prefixo do email pra pré-montar o perfil (o passo de perfil do
    // onboarding carrega esses valores nos inputs e o usuário só ajusta).
    // "Leitor"/"leitor_xxx" fica como último recurso.
    const emailPrefix = email?.split("@")[0]?.trim() || null;
    const derivedName =
      displayName ?? (emailPrefix ? humanizeEmailPrefix(emailPrefix) : null);

    const newUser: UserProfile = {
      uid,
      email: email ?? "",
      username: buildUsername(derivedName, uid),
      displayName: derivedName ?? "Leitor",
      photoURL: photoURL ?? "",
      bio: "",
      themePreference: "default",
      themeId: "default",
      readingGoal: 12,
      lifetimeBooksRead: 0,
      ownedBooksCount: 0,
      favoriteGenres: [],
      onboardingCompleted: false,
      badges: [],
      lastStreak: 0,
      freeStreakRecoveryUsed: false,
      streakTickets: 0,
      followersCount: 0,
      followingCount: 0,
      isPro: false,
      legacyTags: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, newUser);
    await createUserStatsIfMissing(uid);
    return normalizeUserProfile(newUser);
  }

  await createUserStatsIfMissing(uid);
  return normalizeUserProfile(snapshot.data() as UserProfile);
};

/**
 * Ponte de perfil por `@` — entre o toque numa linha de ranking e a página do
 * perfil público (17/09/2026, rodada de performance).
 *
 * Medido: abrir um perfil pela liga custava QUATRO leituras em série antes do
 * primeiro pixel — `users/{uid}` (para descobrir o `@`, no
 * `useProfileNavigation`), depois `usernames/{@}` e `users/{uid}` DE NOVO (em
 * `getUserProfileByUsername`), e ainda a aresta de follow. O mesmo documento ia
 * ao servidor duas vezes seguidas, e a página ficava em skeleton até a última.
 *
 * Quem resolve uid → perfil deixa o perfil aqui por alguns segundos, e
 * `getUserProfileByUsername` o consome: a página abre sem esperar a rede. O TTL
 * é curto e a entrada é consumida (não relida) de propósito — isto é uma ponte
 * de navegação, não um cache de dado; quem precisa do perfil vivo assina
 * `onSnapshot`, e a segunda carga da mesma página vai ao servidor.
 */
const PROFILE_BRIDGE_TTL_MS = 15_000;
const profileBridgeByHandle = new Map<string, { profile: UserProfile; at: number }>();

function primeProfileBridge(profile: UserProfile): void {
  const handle = profile.username?.trim().toLowerCase();
  if (!handle) return;
  profileBridgeByHandle.set(handle, { profile, at: Date.now() });
}

function takeProfileBridge(username: string): UserProfile | null {
  const handle = username.trim().toLowerCase();
  const hit = profileBridgeByHandle.get(handle);
  if (!hit) return null;
  profileBridgeByHandle.delete(handle);
  return Date.now() - hit.at <= PROFILE_BRIDGE_TTL_MS ? hit.profile : null;
}

/** Só para testes. */
export function __resetProfileBridgeForTests(): void {
  profileBridgeByHandle.clear();
}

/**
 * Busca o perfil de um usuário pelo uid.
 *
 * Com teto (BUG-179): esta é a leitura do toque numa linha de ranking, e sem
 * teto um transporte morto virava "toque que não faz nada" para sempre, sem
 * erro. O perfil lido fica na ponte acima para a página que vem em seguida.
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const snapshot = await withReadTimeout(getDoc(doc(db, "users", uid)));
  if (!snapshot.exists()) return null;
  const profile = normalizeUserProfile(snapshot.data() as UserProfile);
  primeProfileBridge(profile);
  return profile;
};

/**
 * Retorna true se o username está disponível (não existe em outra conta).
 * Passa currentUid para excluir a conta própria do check (reeditar o mesmo username).
 */
export async function checkUsernameAvailability(
  username: string,
  currentUid?: string,
): Promise<boolean> {
  // 1 read direto por doc ID (sem query, sem índice) na coleção de reserva.
  // ⚠️ É um PALPITE otimista pra UI: entre este check e o submit, outro
  // usuário pode reservar o mesmo handle. A fonte de verdade é a transação
  // do `claimUsername` — quem chama isto tem que tratar "taken" no submit.
  const snap = await getDoc(doc(db, "usernames", username.toLowerCase()));
  if (!snap.exists()) return true;
  if (currentUid && snap.data()?.uid === currentUid) return true;
  return false;
}

/**
 * Busca o perfil de um usuário pelo username público (/profile/:username).
 */
export const getUserProfileByUsername = async (
  username: string
): Promise<UserProfile | null> => {
  // Perfil que acabou de ser resolvido por uid (toque num ranking/feed): abre
  // na hora, sem as duas leituras abaixo. Ver `primeProfileBridge`.
  const bridged = takeProfileBridge(username);
  if (bridged) return bridged;

  // Resolve pela reserva (`usernames/{handle}` → uid) em vez de query por
  // campo. Além de 1 read por doc ID, corrige o furo do `limit(1)` antigo:
  // com dois docs de `users` carregando o mesmo username, a query devolvia
  // um deles ARBITRARIAMENTE — um usuário sequestrava o perfil público do
  // outro. A reserva torna a colisão impossível na origem.
  const reservation = await withReadTimeout(getDoc(doc(db, "usernames", username.toLowerCase())));
  const uid = reservation.data()?.uid as string | undefined;
  if (!uid) return null;

  const snapshot = await withReadTimeout(getDoc(doc(db, "users", uid)));
  if (!snapshot.exists()) return null;
  return normalizeUserProfile(snapshot.data() as UserProfile);
};

/**
 * Assina o documento do usuário em tempo real (onSnapshot).
 * Usado pelo AuthProvider para manter o profile reativo após transações
 * de gamificação (badges, stats) sem precisar de refreshProfile manual.
 *
 * @returns função de cancelamento (unsubscribe)
 */
/**
 * "Este snapshot pode virar `profile = null`?"
 *
 * `exists() === false` tem DOIS significados e tratar os dois igual foi o que
 * deixou o app inutilizavel na volta do background:
 *
 *  1. **do servidor** — o documento realmente nao existe (conta apagada,
 *     wipe). Entregar `null` e o correto.
 *  2. **do cache** — o SDK ainda nao tem cópia local (offline logo apos a
 *     retomada, IndexedDB indisponivel, cache purgado pelo sistema). Aqui
 *     `null` e uma AFIRMACAO FALSA sobre o servidor.
 *
 * O estrago do caso 2 nao para no perfil: `profile = null` com `isLoading`
 * ja em `false` faz `resolveEquippedThemeId` cair em `"default"`, o
 * `ThemeContext` REMOVE o `data-theme` do `<html>` e o app inteiro vira Linho
 * (papel `#f4f1ec`) — e o `OnboardingGuard` desvia para `/onboarding`. Ou
 * seja: um unico snapshot de cache manda quem ja e usuario para o cadastro,
 * com a tela na cor errada, e **nada re-assina** para desfazer.
 *
 * ⚠️ **Custo assumido:** primeira carga genuinamente offline e sem cópia local
 * deixa de entregar `null` — o callback simplesmente nao vem, o teto do
 * `AuthContext` (`BOOT_PROFILE_SYNC_TIMEOUT`) dispara e a pessoa ve a tela de
 * falha COM "Tentar de novo". Falhar visivel e melhor que fingir que a conta
 * nao tem perfil.
 */
export function shouldDeliverProfileSnapshot(exists: boolean, fromCache: boolean): boolean {
  return exists || !fromCache;
}

export function subscribeToUserProfile(
  uid: string,
  callback: (profile: UserProfile | null) => void,
  onError?: (error: Error) => void
): () => void {
  return resilientOnSnapshot(
    (next, error) => onSnapshot(doc(db, "users", uid), next, error),
    (snap: DocumentSnapshot) => {
      if (!shouldDeliverProfileSnapshot(snap.exists(), snap.metadata.fromCache)) return;
      callback(snap.exists() ? normalizeUserProfile(snap.data() as UserProfile) : null);
    },
    (err) => onError?.(err)
  );
}

/**
 * Espelha `themePreference` em `themeId` no mesmo write.
 *
 * `themeId` é o alias público do tema, e até 04/08 só era escrito na criação
 * da conta e no fim do onboarding — os dois caminhos de equipar tema
 * (`ThemePreviewBar`, `CosmeticPicker`) escrevem apenas `themePreference`.
 * Resultado: quem trocava de tema depois do onboarding continuava aparecendo
 * com o tema antigo para as visitas do perfil.
 *
 * O espelho fica AQUI, e não em cada caminho de equipar, porque este é o
 * choke point de toda escrita client-side em `users/{uid}` — um caminho novo
 * de equipar tema não tem como esquecer. Se o chamador mandar `themeId`
 * explicitamente, a intenção dele ganha.
 */
function withThemeMirror(updates: UpdatableProfile): UpdatableProfile {
  if (typeof updates.themePreference !== "string" || "themeId" in updates) {
    return updates;
  }
  return { ...updates, themeId: updates.themePreference };
}

/**
 * Atualiza campos editáveis do perfil do usuário.
 */
export const updateUserProfile = async (
  uid: string,
  updates: UpdatableProfile
): Promise<void> => {
  await updateDoc(doc(db, "users", uid), {
    ...withThemeMirror(updates),
    updatedAt: serverTimestamp(),
  });
};

// (incrementDailyPagesRead foi APOSENTADO no BUG-18: o mapa users.dailyPages
// só recebia o caminho manual — páginas de foco nunca entravam — e alimentava
// um gráfico que nunca batia com o total. O gráfico semanal agora lê a trilha
// canônica server-side `user_stats/{uid}/daily_pages`; docs antigos mantêm o
// mapa legado sem uso.)

// ---------------------------------------------------------------------------
// Streak — recuperação
// ---------------------------------------------------------------------------
//
// A partir da Sprint E (`docs/analise-evolucao-ux-lura.md` §1.1):
//   - O incremento da streak passou a ser fonte única das CFs
//     `updateBookProgress` e `completeFocusSession` (servidor).
//   - As recuperações também migraram para a callable `recoverStreak`.
//
// Os helpers abaixo expõem a mesma interface antiga para minimizar
// alterações no consumidor (Home), mas internamente chamam a callable.

export {
  recoverStreakWithFreePass,
  recoverStreakWithTicket,
} from "./streakService";

/**
 * Finaliza o onboarding, salvando todas as preferências coletadas nos 5 passos.
 */
export const completeUserOnboarding = async (params: {
  uid: string;
  themePreference: string;
  readingGoal: number;
  lifetimeBooksRead: number;
  ownedBooksCount: number;
  favoriteGenres: string[];
  bio?: string;
  displayName?: string;
  /**
   * BUG-202: `false` quando a conta JÁ concluiu o onboarding. A declaração
   * única (`lifetimeBooksRead`/`ownedBooksCount`) só é aceita pelas rules na
   * transição `onboardingCompleted` false → true; re-enviá-la numa conta
   * concluída faz o `updateDoc` INTEIRO cair em permission-denied. Default
   * `true` = comportamento de sempre para a conta nova.
   */
  declareLifetime?: boolean;
  // NOTA: `username` NÃO entra aqui de propósito. O campo saiu da whitelist
  // do client nas Firestore Rules — escrevê-lo neste updateDoc faria a
  // gravação inteira do onboarding falhar com permission-denied. O @ é
  // reivindicado antes, via `claimUsernameCallable` (Onboarding.tsx).
  //
  // `photoURL` saiu daqui pelo MESMO motivo (28/07, onda de imagens de
  // perfil): virou client-locked. A foto do onboarding é publicada quando o
  // usuário confirma o enquadramento, pela callable `setProfileImage` —
  // antes deste updateDoc, e não junto dele.
}): Promise<void> => {
  const {
    uid, themePreference, readingGoal, lifetimeBooksRead, ownedBooksCount, favoriteGenres, bio, displayName,
    declareLifetime = true,
  } = params;

  // Guard: temas PRO só são aceitos com validação futura.
  // Por ora, qualquer tema fora da lista gratuita é revertido para 'default'.
  //
  // ⚠️ Tema CONQUISTADO é recusado explicitamente, e não por acaso: ele vive
  // dentro de `FREE_THEME_IDS` de propósito (é o que impede o guard de PRO de
  // matá-lo para quem é grátis e ganhou a badge), então a lista sozinha o
  // aceitaria aqui. Uma conta que está TERMINANDO o onboarding não tem badge
  // nenhuma — gravar a preferência faria o resolvedor devolver "default" no
  // primeiro render, que é o downgrade silencioso do BUG-95.
  const safeTheme = isThemeAcceptedAtOnboarding(themePreference) ? themePreference : "default";

  await updateDoc(doc(db, "users", uid), {
    themePreference: safeTheme,
    themeId: safeTheme,
    readingGoal,
    ...(declareLifetime && { lifetimeBooksRead, ownedBooksCount }),
    favoriteGenres,
    ...(bio !== undefined && { bio }),
    ...(displayName !== undefined && { displayName }),
    onboardingCompleted: true,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Resolve UID para convite em listas: aceita UID válido existente ou e-mail (match exato em users.email).
 */
/**
 * Busca usuários por prefixo de username (case-insensitive via lowercase no campo salvo).
 * Usa range query do Firestore: >= term e <= term + '\uf8ff'.
 * Limitado a 50 resultados.
 */
export async function searchUsers(searchTerm: string): Promise<UserProfile[]> {
  const term = searchTerm.toLowerCase().trim();
  if (term.length < 2) return [];
  const q = query(
    collection(db, "users"),
    where("username", ">=", term),
    where("username", "<=", term + "\uf8ff"),
    limit(50),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => normalizeUserProfile(d.data() as UserProfile));
}

/**
 * Atualiza as badges fixadas na vitrine do perfil.
 * Garante o máximo de `MAX_HIGHLIGHTED_BADGES` — slice silencioso se exceder.
 * O corte espelha o teto das Rules: passar do limite é recusa do servidor.
 */
export async function updateHighlightedBadges(uid: string, badgeIds: string[]): Promise<void> {
  await updateDoc(doc(db, "users", uid), {
    highlightedBadges: badgeIds.slice(0, MAX_HIGHLIGHTED_BADGES),
    updatedAt: serverTimestamp(),
  });
}

export async function resolveUserUidForCollaboration(identifier: string): Promise<string | null> {
  const trimmed = identifier.trim();
  if (!trimmed) return null;

  const uidSnap = await getDoc(doc(db, "users", trimmed));
  if (uidSnap.exists()) return trimmed;

  const email = trimmed.toLowerCase();
  const q = query(collection(db, "users"), where("email", "==", email), limit(1));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return snapshot.docs[0].id;
}
